<template>
  <div>
    <v-navigation-drawer
      :value="value"
      temporary
      stateless
      fixed
      right
      width="502"
      @input="(v) => !v && $emit('input', false)"
    >
      <DrawerToolbar
        admin
        :show-back-button="true"
        :show-close-button="false"
        :title="drawerTitle"
        @back="handleClose"
      />
      <v-container class="pa-4">
        <div class="mb-6">
          <Button
            outlined
            iconLeft="mdi-eye"
            data-testid="tab-preview-button"
            @click="handlePreview"
          >
            Preview
          </Button>
        </div>
        <v-form ref="form" class="fields">
          <v-text-field
            v-model="form.tabName"
            outlined
            color="brandPrimary"
            hide-details="auto"
            label="Tab Name*"
            persistent-hint
            hint="Visible as the tab label for participants"
            :rules="tabNameRules"
            data-testid="tab-name-input"
            :counter="50"
          />
          <v-text-field
            v-model="form.title"
            outlined
            color="brandPrimary"
            hide-details="auto"
            label="Title"
            persistent-hint
            hint="Optional title text inside tab body"
            data-testid="tab-title-input"
          />
          <VueEditor
            :value="form.bodyContent"
            :editorToolbar="editorToolbar"
            :editorOptions="editorOptions"
            placeholder="Enter tab content..."
            useCustomImageHandler
            @image-added="handleImageAdded"
            @input="onBodyInput"
          />
          <div v-if="contentError" class="v-text-field__details" data-testid="tab-content-error">
            <div class="v-messages theme--light error--text" role="alert">
              <div class="v-messages__wrapper">
                <div class="v-messages__message">Tab content is required</div>
              </div>
            </div>
          </div>
          <div
            v-if="contentLengthError"
            class="v-text-field__details"
            data-testid="tab-content-length-error"
          >
            <div class="v-messages theme--light error--text" role="alert">
              <div class="v-messages__wrapper">
                <div class="v-messages__message">Tab content must be 4000 characters or fewer</div>
              </div>
            </div>
          </div>
          <Typography variant="body-2" class="banner-description">
            Images are supported with a max file size of 10 MB.
          </Typography>
        </v-form>
      </v-container>
      <v-footer color="white" class="d-flex justify-end pa-4" style="gap: 16px">
        <Button text data-testid="cancel-btn" @click="handleClose">Cancel</Button>
        <Button data-testid="submit-btn" :disabled="submitting" @click="handleSubmit">
          {{ tab ? "Update Tab" : "Add Tab" }}
        </Button>
      </v-footer>
      <LoadingDialog :showDialog="uploading" header="Uploading image..." />
      <ErrorDialogV2
        v-model="showCloseConfirm"
        title="Do you want to save any changes you made before you close?"
        confirm-button-text="Save"
        cancel-button-text="Don't save"
        show-cancel-button
        @confirm="handleCloseConfirmSave"
        @cancel="handleDiscardAndClose"
      />
    </v-navigation-drawer>
    <v-overlay :value="value" />
  </div>
</template>

<script>
import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import Button from "@/components/generic/Button.vue";
import { VueEditor } from "vue2-editor";
import { editorToolbarWithImages, editorOptionsWithImages } from "@/shared_data/data";
import ContentService from "@/services/ContentService";
import { uploadEmbeddedContent } from "@/utils/embeddedContentHelper";
import Typography from "@/components/generic/Typography.vue";
import LoadingDialog from "@/components/loadingDialog.vue";
import ErrorDialogV2 from "@/components/ErrorDialogV2.vue";
import { mapState } from "vuex";

const MAX_IMAGE_SIZE_MB = 10;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

export default {
  name: "TabEditorSideSheet",
  components: { DrawerToolbar, Button, VueEditor, Typography, LoadingDialog, ErrorDialogV2 },
  props: {
    value: { type: Boolean, default: false },
    tab: { type: Object, default: null },
  },
  data() {
    return {
      submitting: false,
      contentError: false,
      contentLengthError: false,
      showCloseConfirm: false,
      editorToolbar: editorToolbarWithImages,
      editorOptions: editorOptionsWithImages,
      uploading: false,
      form: { tabName: "", title: "", bodyContent: "" },
      tabNameRules: [
        (v) => !!(v && v.trim()) || "Tab name is required",
        (v) => (v && v.trim().length <= 50) || "Tab name must be 50 characters or fewer",
      ],
    };
  },
  computed: {
    ...mapState(["globalClientId"]),
    clientId() {
      return this.$route.query.clientId ?? this.globalClientId;
    },
    drawerTitle() {
      return this.tab ? "Edit Tab" : "Add Tab";
    },
    isDirty() {
      if (this.tab) {
        return (
          this.form.tabName !== (this.tab.tabName || "") ||
          this.form.title !== (this.tab.title || "") ||
          this.form.bodyContent !== (this.tab.bodyContent || "")
        );
      }
      return (
        this.form.tabName !== "" ||
        this.form.title !== "" ||
        !this.isBodyEmpty(this.form.bodyContent)
      );
    },
  },
  watch: {
    tab: {
      immediate: true,
      handler() {
        this.resetForm();
      },
    },
    value(newVal) {
      if (newVal) {
        this.submitting = false;
        this.contentError = false;
        this.contentLengthError = false;
        this.$nextTick(() => {
          if (this.$refs.form && this.$refs.form.resetValidation) {
            this.$refs.form.resetValidation();
          }
        });
      }
    },
  },
  methods: {
    resetForm() {
      this.submitting = false;
      this.contentError = false;
      this.contentLengthError = false;
      if (this.tab) {
        this.form = {
          tabName: this.tab.tabName || "",
          title: this.tab.title || "",
          bodyContent: this.tab.bodyContent || "",
        };
      } else {
        this.form = { tabName: "", title: "", bodyContent: "" };
      }
    },
    handleClose() {
      if (this.isDirty) {
        this.showCloseConfirm = true;
        return;
      }
      this.$emit("input", false);
    },
    handleCloseConfirmSave() {
      this.showCloseConfirm = false;
      this.handleSubmit();
    },
    handleDiscardAndClose() {
      this.showCloseConfirm = false;
      this.$emit("input", false);
    },
    async handleImageAdded(file, Editor, cursorLocation, resetUploader) {
      if (this.uploading) {
        this.$store.dispatch("showError", {
          message: "Please wait for the current image to finish uploading",
        });
        resetUploader?.();
        return;
      }

      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        this.$store.dispatch("showError", {
          message: `Image must be under ${MAX_IMAGE_SIZE_MB}MB`,
        });
        resetUploader?.();
        return;
      }

      this.uploading = true;
      try {
        const attachment = {
          name: "Program Detail Tab Content",
          description: "Image used within the content of a program detail tab.",
          category: "LEARNING_CARD_CONTENT",
          clientId: this.clientId,
          file,
          generateSignedUrl: true,
          type: "image",
        };
        const response = await ContentService.createContent(attachment, 2);
        Editor.insertEmbed(cursorLocation, "image", response.contentUrlSigned);

        // Tag with contentId so uploadEmbeddedContent can normalize at save time
        const imgs = Editor.root.querySelectorAll("img:not([id])");
        for (const img of imgs) {
          if (img.src === response.contentUrlSigned) {
            img.setAttribute("id", `contentId-${response.contentId}`);
            break;
          }
        }
      } catch (e) {
        console.error("Failed to upload image", e);
        this.$store.dispatch("showError", {
          message: "Failed to upload image. Please try again.",
        });
      } finally {
        this.uploading = false;
        resetUploader?.();
      }
    },
    isBodyEmpty(html) {
      if (!html) return true;
      const trimmed = html.trim();
      return trimmed === "" || trimmed === "<p><br></p>" || trimmed === "<p><br/></p>";
    },
    handlePreview() {
      const valid = this.$refs.form.validate();
      const bodyIsEmpty = this.isBodyEmpty(this.form.bodyContent);
      if (bodyIsEmpty) this.contentError = true;
      if (!valid || bodyIsEmpty) return;
      this.$emit("preview", this.form);
    },
    onBodyInput(val) {
      this.form.bodyContent = val;
      if (!this.isBodyEmpty(val)) this.contentError = false;
      this.contentLengthError = false;
    },
    async handleSubmit() {
      if (this.submitting) return;
      if (!this.$refs.form.validate()) return;

      if (this.isBodyEmpty(this.form.bodyContent)) {
        this.contentError = true;
        return;
      }

      this.submitting = true;

      let normalized;
      try {
        normalized = await uploadEmbeddedContent(this.form.bodyContent, this.clientId);
      } catch (e) {
        console.error("Failed to process tab images", e);
        this.submitting = false;
        this.$store.dispatch("showError", {
          message: "Failed to process tab images. Please check your connection and try again.",
        });
        return;
      }

      if (normalized.content && normalized.content.length > 4000) {
        this.submitting = false;
        this.contentLengthError = true;
        return;
      }

      const payload = {
        tabName: this.form.tabName,
        title: this.form.title || null,
        bodyContent: normalized.local,
      };

      if (this.tab && this.tab.tabId != null) {
        payload.tabId = this.tab.tabId;
      }

      this.$emit("submit", payload);
      this.$emit("input", false);
      this.form = { tabName: "", title: "", bodyContent: "" };
      this.contentError = false;
      this.contentLengthError = false;
      this.submitting = false;
    },
  },
};
</script>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

::v-deep .ql-editor,
::v-deep .ql-toolbar {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
}
</style>
